import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Log } from '@/utils/Log';
import {
  Form,
  Input,
  Radio,
  Switch,
  Button,
  MessagePlugin,
  DatePicker,
  DialogPlugin,
  Table,
  Space,
  Dialog,
  Link,
  Typography,
  Divider,
} from 'tdesign-react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
} from 'tdesign-icons-react';
import { FEATURE_MINIMAL_VERSION, MACHINE_FEATURES } from '../../config';
import cron from 'cron-validate';
import dayjs from 'dayjs';
import {
  CommonCpuTask,
  CommonMemoryTask,
  DiskTracingTask,
  // DynamicDetectionTask,
  GolangCpuTask,
  GolangMemoryTask,
  JavaCpuTask,
  JavaHeapDumpTask,
  JavaMemoryCompositeTask,
  JavaMemoryTask,
  PageFaultTask,
  BoltTask,
  PythonCpuTask,
  PythonMemoryTask,
  GperfToolsCpuTask,
  GperfToolsMemTask,
  ScriptExecTask,
  // TimingAnalysisTask,
} from './tasks';
import '@tencent/tea-component/dist/tea.css';
import axios from 'axios';
import { HOST_URL, TASK_TYPE_HELP, SCRIPT } from '@/config';
import { goodStatus } from '@/utils/ErrorConfirm';
import { getOAName } from '@/utils/Storage';

const { FormItem } = Form;
const { Text } = Typography;

// 封装visable属性
const VisFormItem = props => (props.visable ? props.children : '');

VisFormItem.propTypes = {
  visable: PropTypes.bool,
};

VisFormItem.defaultProps = {
  visable: false,
};

// 任务类型选择器
// Todo: 如果需要支持按照agent版本功能的话，可以在选择agent后更新taskTypesSelector
const taskTypesSelector = [
  {
    label: '通用分析',
    children: [
      CommonCpuTask,
      CommonMemoryTask,
      PageFaultTask,
    ],
  },
  {
    label: 'Golang分析',
    children: [
      GolangCpuTask,
      GolangMemoryTask,
    ],
  },
  {
    label: 'Java分析',
    children: [
      JavaCpuTask,
      JavaMemoryTask,
      JavaHeapDumpTask,
      JavaMemoryCompositeTask,
    ],
  },
  {
    label: 'Python分析',
    children: [
      PythonCpuTask,
      PythonMemoryTask,
    ],
  },
  {
    label: '编译优化',
    children: [
      BoltTask,
    ],
  },
  {
    label: '用户态采样（适用容器无权限）',
    children: [
      GperfToolsCpuTask,
      GperfToolsMemTask,
    ],
  },
  // TimingAnalysisTask,
  DiskTracingTask,
  // DynamicDetectionTask,
  {
    label: '综合分析',
    children: [
      ScriptExecTask,
    ],
  },
];

const versionToNumber = (version) => {
  let result = 0;
  if (version === undefined || version === '') {
    return result;
  }
  const versionArray = version.replace(/^v/, '').replace(/\n$/, '')
    .split('.');
  for (let index = 0; index < versionArray.length; index++) {
    result = result * 100 + parseInt(versionArray[index], 10);
  }
  Log.debug('version', version, 'version number', result);
  return result;
};

const featureEnable = (version, minimalVersion) => versionToNumber(version) >= minimalVersion;

const StartTaskForm = (props) => {
  console.log(TASK_TYPE_HELP);
  const getDefaultAgent = () => {
    const onlineAgents = props.agents.filter(agent => agent.online);
    // 若用户只有一个在线的 agent，则默认选择该 agent
    if (onlineAgents.length === 1) {
      return onlineAgents[0];
    }
    return null;
  };

  const options = [];
  props.agents.map((agent) => {
    if (agent.online) {
      const unavailableFeatures = new Set();
      agent.unavailable_features?.forEach((feature) => {
        // 将 feature 的数字后两位置置为 00(这两位为具体原因，不需要用于判断)
        unavailableFeatures.add(feature - (feature % 100));
      });

      let version = '版本过低';
      if (agent.version) {
        version = `v${agent.version}`;
      }

      options.push({
        label: `${agent.ipaddr} [${agent.hostname}]`,
        title: `${agent.ipaddr} [${agent.hostname}]`,
        value: agent.ipaddr,
        version,
        unavailableFeatures,
        key: agent.ipaddr,
      });
    }
    return 0;
  });
  const [taskTypes, setTaskTypes] = useState(0); // 子任务列别
  const [currentFormItemIndex, setCurrentFormItemIndex] = useState(0);
  const [currentFormItemSelector, setCurrentFormItemSelector] = useState(taskTypesSelector[0].children[0]);
  const [currentAgent, setCurrentAgent] = useState({
    // version 为已选择的agents中最小的 version，用于 featureEnable 判断
    version: getDefaultAgent()?.version || '',
    unavailableFeatures: (getDefaultAgent()?.unavailable_features && new Set(getDefaultAgent().unavailable_features))
      || new Set(),
  });
  const [curlCommand, setCurlCommand] = useState('');
  const [showCurlCommand, setShowCurlCommand] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showScriptTemplate, setShowScriptTemplate] = useState(false);
  const [filterValue, setFilterValue] = useState({ label: '' });
  const [filteredOptions] = useState(options);
  const [selectTime, setSelectTime] = useState(dayjs());

  const noUnavailableFeatures = (...features) => {
    if (!currentAgent.unavailableFeatures) {
      return true;
    }
    for (const feature of features) {
      if (currentAgent.unavailableFeatures.has(feature)) {
        return false;
      }
    }
    return true;
  };

  const [form] = Form.useForm();
  const perfWidecommand = Form.useWatch('perf_wide', form);
  const tracingWidecommand = Form.useWatch('tracing_wide', form);

  useEffect(() => {
    // 根据 perf_wide_command 和 tracing_wide_command 的值更新 duration 的初始值
    const initialDuration = perfWidecommand === 'command' || tracingWidecommand === 'command' ?  '' : 10;
    form.setFieldsValue({ duration: initialDuration });
  }, [perfWidecommand, tracingWidecommand, form]);

  const changeTaskGeneralType = (value) => {
    Log.debug('changeTaskGeneralType', taskTypesSelector[value]);
    setTaskTypes(value);
    if (taskTypesSelector[value].children) {
      changeTaskType(0);
    }
  };

  const changeTaskType = (value) => {
    setCurrentFormItemIndex(value);
  };

  useEffect(() => {
    if (localStorage.getItem('key') !== null) {
      return;
    }
    axios({
      method: 'get',
      url: `${HOST_URL}/api/v1/users`,
      params: {
        user: getOAName(),
      } })
      .then((response) => {
        if (goodStatus(response)) {
          window.localStorage.setItem('key', response?.data?.key);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    let selector;
    if (taskTypesSelector[taskTypes].children) {
      selector = taskTypesSelector[taskTypes].children[currentFormItemIndex];
      form.setFieldsValue({ name: `${taskTypesSelector[taskTypes].label}-${selector.label}` });
    } else {
      selector = taskTypesSelector[taskTypes];
      form.setFieldsValue({ name: selector.label });
    }
    setCurrentFormItemSelector(selector);
    Log.debug('currentFormItemSelector', taskTypes, currentFormItemIndex, selector);
  }, [taskTypes, currentFormItemIndex]);

  // 提交数据
  const onSubmit = (e) => {
    const fields = form.getFieldsValue(true);
    Log.debug(e);
    Log.debug('fields', fields);
    if (e.validateResult !== true) {
      MessagePlugin.error('请正确填写表单');
      return;
    }

    const submit = () => {
      props.onClick(Object.assign(fields, {
        task_type: currentFormItemSelector.task_type,
        profiler_type: currentFormItemSelector.profiler_type,
        event: currentFormItemSelector.event,
        target_host: selectedRowKeys,
        // 传递下钻任务相关参数
        drilldown_enable: props.presetParams?.drilldown_enable || false,
        master_tid: props.presetParams?.master_tid || '',
      }));
    };
    const profilingThreshold = 120 * 1299; // 120s * 1299Hz

    // 当Agent未有资源预检特性时，对超过阈值的任务进行警告
    if (!featureEnable(currentAgent.version, FEATURE_MINIMAL_VERSION.resource_precheck)
      && (parseInt(fields.duration, 10) * parseInt(fields.profiling_freqence, 10) > profilingThreshold)) {
      const confirmDia = DialogPlugin.confirm({
        header: '继续执行任务？',
        body: <>当前任务的执行时长和频率较高，可能对目标机器会造成较长时间的负载。是否继续执行任务? <br/><br/>
          <a
            href="https://iwiki.woa.com/p/4009694046"
            target="_blank" rel="noreferrer">升级Agent</a>至1.3.1及以上版本, 可自动启用资源预估特性，在机器资源不足时自动中断采样任务。
          <br/><br/>
          Agent开销评估：<a
            href="https://iwiki.woa.com/p/4009585311"
            target="_blank" rel="noreferrer">iWiki</a>
        </>,
        confirmBtn: '确认',
        cancelBtn: '取消',
        onConfirm: () => {
          submit();
          confirmDia.hide();
        },
        onClose: () => {
          confirmDia.hide();
        },
      });

      return;
    }

    submit();
  };

  // 生成命令
  const genCurlCommand = () => {
    const fields = form.getFieldsValue(true);
    const command = props.genCurlCommand(Object.assign(fields, {
      task_type: currentFormItemSelector.task_type,
      profiler_type: currentFormItemSelector.profiler_type,
      event: currentFormItemSelector.event,
      target_host: selectedRowKeys,
    }), window.localStorage.getItem('key'));
    console.log(curlCommand);
    setCurlCommand(String(command));
    setShowCurlCommand(true);
  };

  // 重置表单
  const onReset = (e) => {
    console.log(e);
    changeTaskGeneralType(0);
    setCurrentAgent({
      version: '',
    });
    MessagePlugin.info('重置成功');
  };

  const columns = [
    { colKey: 'choice', type: 'multiple', width: '35' },
    { colKey: 'label', title: '主机(右侧筛选)', width: '260',
      filter: {
        type: 'input',
        // 文本域搜索
        // component: Textarea,

        resetValue: '',
        // 按下 Enter 键时也触发确认搜索
        confirmEvents: ['onEnter'],
        props: {
          placeholder: '输入关键词过滤',
        },
        // 是否显示重置取消按钮，一般情况不需要显示
        showConfirmAndReset: true,
      } },
    { colKey: 'version', title: '版本号', width: '60' },
  ];
  const onFilterChange = (value) => {
    console.log(value);
    setFilterValue(value);
    // setFilteredOptions(options.filter((option) => {
    //   return option.label.includes(value.label);
    // }));
  };

  const [selectedRowKeys, setSelectedRowKeys] = useState(getDefaultAgent() ? [getDefaultAgent().ipaddr] : []);

  // 如果有预设参数，初始化表单
  useEffect(() => {
    Log.debug('presetParams', props.presetParams);
    if (props.presetParams) {
      // eslint-disable-next-line camelcase
      const { name, perf_wide, duration, target_host, profiling_freqence } = props.presetParams;

      // 设置表单初始值
      if (name) form.setFieldsValue({ name });
      // eslint-disable-next-line camelcase
      if (perf_wide) form.setFieldsValue({ perf_wide });
      if (duration) form.setFieldsValue({ duration });
      // eslint-disable-next-line camelcase
      if (profiling_freqence) form.setFieldsValue({ profiling_freqence });

      // 设置目标主机
      // eslint-disable-next-line camelcase
      if (target_host && Array.isArray(target_host) && target_host.length > 0) {
        setSelectedRowKeys(target_host);
      }

      Log.debug('预设参数已应用', props.presetParams);
    }
  }, [props.presetParams]);

  const onSelectChange = (value) => {
    Log.debug('choose value', value);
    setSelectedRowKeys(value);

    let minVersion = '100.0.0';
    if (value.length > 0 && options.length > 0) {
      for (const option of options) {
        if (value.includes(option.value) && option.version
          && versionToNumber(minVersion) > versionToNumber(option.version)) {
          minVersion = option.version;
        }
      }
    }

    // 不可用特性计算为并集
    const unavailableFeatures = new Set();
    value.forEach((a) => {
      options[a]?.unavailableFeatures.forEach((feature) => {
        unavailableFeatures.add(feature);
      });
    });

    setCurrentAgent({
      version: minVersion,
      unavailableFeatures,
    });
  };

  const disableTime = () => {
    if (selectTime.format('MM-DD') === dayjs().format('MM-DD')) {
      return {
        hour: [...Array(dayjs().hour()).keys()],
        minute: [...Array(dayjs().minute() + 1).keys()],
      };
    }
    return {};
  };
  const forbiddenStrings = ['route', 'ifconfig', 'ping', 'dhclient', 'net',
    'iptables', 'rfcomm', 'wvdial', 'iwconfig', 'miitool',
    'rpm', 'update', 'yum',
    'service', 'chkconfig', 'systemctl start', 'systemctl stop', 'systemctl reload', 'systemctl restart',
    'systemctl status', 'systemctl enable', 'systemctl disable', 'updatedb',
    'fdisk', 'sfdisk', 'parted', 'partprobe', 'mount', 'umount',
    'visudo', 'chown', 'chmod', 'chgrp',
    'nice', 'kill', 'killall', 'modprobe', 'curl'];

  // 为每个不允许的字符串创建一个正则表达式规则
  const forbiddenPatterns = forbiddenStrings.map(str => ({
    pattern: new RegExp(`^(?:(?!${str}).)*$`), // 使用否定前视断言确保不包含指定字符串
    message: `请勿使用权限指令 "${str}"`,
  }));

  // GperfTools 启动脚本模板
  const _scriptTemplate = `#!/bin/bash

set -e

PROFILER_LIB="/usr/local/drop/lib/libprofiler.so:/usr/local/drop/lib/libjemalloc.so:/usr/local/drop/lib/liballoc_hook.so"
CPU_PROFILE_PATH="/tmp/drop_pprof.data"
HEAP_PROFILE_PATH="/tmp/drop_pprof.heap.data"

TARGET_PROGRAM="*************************"
# 如有参数
# ARGS=("--first-argument" "value1" "--second-argument" "value2")

# 启动程序 - 同时启用 heap 和 CPU profiling
exec env LD_PRELOAD="$PROFILER_LIB" \\
    MALLOC_CONF="prof:true,prof_active:true,prof_prefix:\${HEAP_PROFILE_PATH}" \
    CPUPROFILE="$CPU_PROFILE_PATH" \\
    CPUPROFILESIGNAL=12 \\
    "$TARGET_PROGRAM" \\
# 如有参数
#   "\${ARGS[@]}"`;

  return (
    <>
    <Form
      form={form}
      layout="vertical"
      onSubmit={onSubmit}
      labelWidth={100}
      onReset={onReset}
      scrollToFirstError="smooth"
      resetType='initial'
    >
      <FormItem label="任务名" name="name" initialData={'通用采样分析'}
                rules={[{ required: true, type: 'error', message: '请输入任务名' }]}>
        <Input/>
      </FormItem>
      <FormItem
        label="目标主机"
        name="target_host"
        rules={[
          {
            validator: () => !(form.getFieldsValue(true).task_general_type === 'multi' && selectedRowKeys.length > 1),
            message: '动态检测任务只允许指定一个目标主机', // 动态监测不允许发起批量任务，否则会导致复合任务中有复合任务
          },
          {
            validator: () => {
              // 如果是Golang分析（taskTypes === 1），则不需要选择主机
              if (taskTypes === 1) {
                return true;
              }
              return selectedRowKeys.length >= 1;
            },
            message: '请选择主机',
          },
        ]}
        help={taskTypes === 1 ? 'Golang分析任务可以不选择主机直接发起' : ''}
      >
        <Table
          size="small"
          rowKey="key"
          data={filteredOptions.filter(agent => agent.label.includes(filterValue.label))}
          columns={columns}
          showHeader={true}
          selectOnRowClick={true}
          selectedRowKeys={selectedRowKeys}
          onSelectChange={onSelectChange}
          maxHeight={210}
          lazyLoad
          bordered
          // filterValue={filterValue}
          // defaultFilterValue={filterValue}
          onFilterChange={onFilterChange}
        />
      </FormItem>
      <FormItem label="任务类型" name="task_general_types">
        <Space direction="vertical">
          {<>
            <Radio.Group value={taskTypes} onChange={(v) => {
              changeTaskGeneralType(v);
            }}>
              {taskTypesSelector.map((tasktype, index) => <Radio.Button key={index} value={index} >
                {tasktype.label}
              </Radio.Button>)}
            </Radio.Group>
          </>}
        </Space>
      </FormItem>
      {taskTypesSelector[taskTypes].children ? <FormItem label="详细类型" name="task_detail_types"
      help={ TASK_TYPE_HELP[currentFormItemSelector.task_type] ? TASK_TYPE_HELP[currentFormItemSelector.task_type]
        : '' }>
        <Space direction="vertical">
          {taskTypesSelector[taskTypes].children
            ? <>
              <Radio.Group value={currentFormItemIndex} onChange={changeTaskType}>
                {taskTypesSelector[taskTypes].children.map((tasktype, index) => <Radio.Button
                  key={index} value={index}>
                  {tasktype.label}
                </Radio.Button>)}
              </Radio.Group>
              {taskTypes === 3 && currentFormItemIndex === 1 && (
                <Text theme="warning" size="small" style={{ marginTop: 8 }}>
                  当前仅支持python3.7+哦（暂时不建议在线上环境执行）～
                </Text>
              )}
              {taskTypes === 5 && (
                <div style={{ marginTop: 16 }}>
                  <Button
                    theme="default"
                    variant="outline"
                    onClick={() => setShowScriptTemplate(true)}
                    style={{ marginBottom: 8 }}
                  >
                    获取执行脚本模板
                  </Button>
                  <Text theme="warning" size="small" style={{ display: 'block', marginTop: 8 }}>
                    用户态采样需要重启进程，可点击获取模板，使用脚本重启，如果已经按照脚本重启则无需再次重启
                  </Text>
                </div>
              )}
            </> : null}
        </Space>
      </FormItem> : null}
      <VisFormItem visable={Form.useWatch('profiler_type', form) !== 2 && !!currentFormItemSelector.perf_wide}>
        <FormItem label="查询范围" name="perf_wide" initialData='system'
                  rules={[{ required: true, type: 'error', message: '请选择采样范围' }]} help={
          !currentFormItemSelector.java_profiler_type && !currentFormItemSelector.java_heap_task ? null : '若未指定pid，将自动执行 pgrep -n java 命令获取pid'
        }>
          <Radio.Group>
            <Radio.Button
              value="system">{!currentFormItemSelector.java_profiler_type && !currentFormItemSelector.java_heap_task ? 'System' : '最近启动的Java进程'}</Radio.Button>
            <Radio.Button value="pid">Pid</Radio.Button>
            <VisFormItem visable={!!currentFormItemSelector.callgraph
              && noUnavailableFeatures(MACHINE_FEATURES.OPTION_COMM)}>
              <Radio.Button value="comm">进程名</Radio.Button>
            </VisFormItem>
            <VisFormItem visable={!!currentFormItemSelector.callgraph
              && noUnavailableFeatures(MACHINE_FEATURES.OPTION_PORT)}>
              <Radio.Button value="port">端口</Radio.Button>
            </VisFormItem>
            <VisFormItem visable={!!currentFormItemSelector.callgraph
              && noUnavailableFeatures(MACHINE_FEATURES.OPTION_EXE)}>
              <Radio.Button value="command">可执行文件</Radio.Button>
            </VisFormItem>
            <VisFormItem visable={!!currentFormItemSelector.callgraph}>
              <Radio.Button value="tid">Tid</Radio.Button>
            </VisFormItem>
          </Radio.Group>
        </FormItem>
      </VisFormItem>
      <VisFormItem visable={!!currentFormItemSelector.tracing_wide}
                  rules={[{ required: true, type: 'error', message: '请选择追踪范围' }]}>
        <FormItem label="追踪范围" name="tracing_wide" initialData='pid'>
          <Radio.Group>
            <Radio.Button value="pid">Pid</Radio.Button>
            <Radio.Button value="comm">进程名</Radio.Button>
            <VisFormItem visable={!!currentFormItemSelector.command_start}><Radio.Button  value="command">可执行文件</Radio.Button></VisFormItem>
            <VisFormItem visable={!!currentFormItemSelector.jeprof_start}><Radio.Button  value="jeprof">TDSQL DB&PROXY</Radio.Button></VisFormItem>
          </Radio.Group>
        </FormItem>
      </VisFormItem>
      <VisFormItem
        visable={Form.useWatch('perf_wide', form) === 'pid'
        | Form.useWatch('tracing_wide', form) === 'pid'
        | currentFormItemSelector.optimize_wide
        | currentFormItemSelector.pyspy_profiler_path
        | currentFormItemSelector.task_type === 10
        | currentFormItemSelector.task_type === 11}>
        <FormItem label="Pid" name="pid"
                  initialData={currentFormItemSelector.pyspy_profiler_path || currentFormItemSelector.task_type === 10 || currentFormItemSelector.task_type === 11 ? '' : -1}
                  rules={
                    currentFormItemSelector.pyspy_profiler_path || currentFormItemSelector.task_type === 10
                    || currentFormItemSelector.task_type === 11
                      ? [{ pattern: /^[1-9]\d*$/, message: '请输入正整数' }, {
                        required: true,
                        type: 'error',
                        message: 'Python任务必须指定进程PID',
                      }]
                      : [{ pattern: /^-1$|^[1-9]\d*$/, message: '请输入正整数' }, {
                        required: true,
                        type: 'error',
                        message: '请输入pid',
                      }]
                  }
                  help={currentFormItemSelector.pyspy_profiler_path || currentFormItemSelector.task_type === 10 || currentFormItemSelector.task_type === 11 ? 'Python任务必须指定目标进程的PID' : ''}>
          <Input placeholder={currentFormItemSelector.pyspy_profiler_path || currentFormItemSelector.task_type === 10 || currentFormItemSelector.task_type === 11 ? '请输入目标Python进程的PID' : ''}/>
        </FormItem>
      </VisFormItem>
      <VisFormItem visable={!!currentFormItemSelector.gperftools_pid}>
        <FormItem label="PID" name="pid"
                  rules={[
                    {
                      pattern: /^[1-9]\d*$/,
                      message: '请输入正整数',
                    },
                    {
                      required: true,
                      type: 'error',
                      message: '请输入目标进程的PID',
                    }]
                  }
                  help="请输入需要采样的目标进程PID">
          <Input placeholder="请输入目标进程的PID"/>
        </FormItem>
      </VisFormItem>
      <VisFormItem
        visable={Form.useWatch('perf_wide', form) === 'comm' | Form.useWatch('tracing_wide', form) === 'comm'}>
        <FormItem label="进程名" name="comm"
                  rules={[{ pattern: /^[a-zA-Z0-9-_]+$/, message: '进程名不应有屏蔽字符' },
                    { required: true, type: 'error', message: '请输入进程名,应只对应一个进程' }]}>
          <Input/>
        </FormItem>
      </VisFormItem>
      <VisFormItem visable={Form.useWatch('perf_wide', form) === 'port' & !currentFormItemSelector.pprof_listen}>
        <FormItem label="端口" name="port"
                  rules={[{ pattern: /^-1$|^[1-9]\d*$/, message: '请输入正整数' }, {
                    required: true,
                    type: 'error',
                    message: '请输入端口',
                  }]}>
          <Input/>
        </FormItem>
      </VisFormItem>
      <VisFormItem visable={Form.useWatch('perf_wide', form) === 'tid'}>
        <FormItem label="Tid" name="tids"
                  rules={[
                    { pattern: /^[1-9]\d*(,[1-9]\d*)*$/, message: '请输入正整数或逗号分隔的正整数，如: 123 或 123,456,789' },
                    { required: true, type: 'error', message: '请输入线程ID' }
                  ]}
                  help="支持单个线程ID或多个线程ID(用逗号分隔)，示例: 123 或 123,456,789">
          <Input placeholder="请输入线程ID，多个用逗号分隔"/>
        </FormItem>
      </VisFormItem>
      <VisFormItem visable={((Form.useWatch('perf_wide', form) === 'command' | Form.useWatch('tracing_wide', form) === 'command') && (!!currentFormItemSelector.command_start)) || (!!currentFormItemSelector.gperftools_pid && !!currentFormItemSelector.command_start)}>
        <FormItem label={currentFormItemSelector.gperftools_pid ? "二进制路径" : "执行路径"} name="command"
                  rules={[
                    ...forbiddenPatterns, // 添加多个 pattern 规则
                    { pattern: /^\/[\w\-.+]+(\/[\w\s\w-.+]+)*\/?$/, message: '请输入正确的文件目录' },
                    { required: true, type: 'error', message: currentFormItemSelector.gperftools_pid ? '请输入二进制文件路径' : '请输入执行路径' },
                  ]}
                  help={currentFormItemSelector.gperftools_pid ? "必填：用于生成携带符号信息的采样文件，示例：/usr/local/bin/myapp" : "示例：/data/temp -o output.data ('/data/temp' 为可执行文件，'-o output.data'为参数)"}>
          <Input placeholder={currentFormItemSelector.gperftools_pid ? "请输入二进制文件的完整路径" : ""}/>
        </FormItem>
      </VisFormItem>
      <VisFormItem visable={currentFormItemSelector.pprof_listen}>
        <FormItem label="IP地址" name="pprof_ip" initialData={'127.0.0.1'}
                  help={<span>
                    填写pprof服务IP地址，默认为127.0.0.1
                  </span>}
                  rules={
                    [{
                      pattern: /^((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)$/,
                      message: '请输入有效的IPv4地址',
                    }, {
                      required: true,
                      type: 'error',
                      message: '请输入IP地址',
                    }]
                  }>
          <Input placeholder="127.0.0.1"/>
        </FormItem>
      </VisFormItem>
      <VisFormItem visable={currentFormItemSelector.pprof_listen}>
        <FormItem label="端口" name="port"
                  help= {  <span>
                    填写pprof监听端口，trpc-go框架请填写admin端口（详情见：
                    <a href="https://iwiki.woa.com/p/4009437334" target="_blank" rel="noopener noreferrer">
                    https://iwiki.woa.com/p/4009437334
                    </a>
                    ）
                  </span>}
                  rules={
                    [{ pattern: /^-1$|^[1-9]\d*$/, message: '请输入正整数' }, {
                      required: true,
                      type: 'error',
                      message: '请输入端口',
                    }]
                  }>
          <Input/>
        </FormItem>
      </VisFormItem>
      <VisFormItem visable={Form.useWatch('tracing_wide', form) === 'jeprof'}>
        <FormItem label="端口" name="port"
                  help= {  <span>
                    proxy需要进行额外配置，详情见：
                    <a href="https://iwiki.woa.com/p/4013370121" target="_blank" rel="noopener noreferrer">
                    https://iwiki.woa.com/p/4013370121
                    </a>
                  </span>}
                  rules={[{ pattern: /^-1$|^[1-9]\d*$/, message: '请输入正整数' }, {
                    required: true,
                    type: 'error',
                    message: '请输入端口',
                  }]}>
          <Input/>
        </FormItem>
      </VisFormItem>

      <VisFormItem visable={!!currentFormItemSelector.trace_object & Form.useWatch('tracing_wide', form) !== 'jeprof' }>
        <FormItem label="内存分配器" name="trace_object" initialData={''}
                  rules={[{ pattern: /^\S*$/, message: '内存分配器位置中不能包含空格' },
                    { required: false, type: 'error', message: '请指定使用的内存分配器位置' }]}
                  help="默认使用 glibc。如果使用了第三方内存分配器 jemalloc、tcmalloc 等，请指定动态库完整路径，例如 /path/to/libjemalloc.so 注意，如果静态链接了内存分配器，请填写 Pid 对应二进制文件的完整路径。">
          <Input placeholder="glibc"/>
        </FormItem>
      </VisFormItem>
      <VisFormItem visable={currentFormItemSelector.task_type === 13}>
        <FormItem label="时长(秒)" name="duration" initialData={30}
                  rules={[{ pattern: /^[1-9]\d*$/, message: '请输入正整数' }, {
                    required: true,
                    type: 'error',
                    message: '请输入采样时长',
                  }]}
                  help="采样持续时间，单位为秒">
          <Input placeholder="30"/>
        </FormItem>
      </VisFormItem>
      <VisFormItem visable={currentFormItemSelector.duration & Form.useWatch('tracing_wide', form) !== 'jeprof' & currentFormItemSelector.task_type !== 13}>
        {Form.useWatch('perf_wide', form) === 'command' | Form.useWatch('tracing_wide', form) === 'command'
          ? <FormItem label="时长(秒)" name="duration" initialData={1}
                  rules={[{ pattern: /^[1-9]\d*$/, message: '请输入正整数' }, {
                    required: false,
                  }]}>
          <Input placeholder="采集默认持续到进程结束,如有需求请输入时长"/>
        </FormItem>
          : <FormItem label="时长(秒)" name="duration" initialData={10}
                rules={[{ pattern: /^[1-9]\d*$/, message: '请输入正整数' }, {
                  required: true,
                  type: 'error',
                  message: '请输入正整数',
                }]}>
              <Input/>
          </FormItem>
        }
      </VisFormItem>
      <VisFormItem
        visable={
          Form.useWatch('profiler_type', form) !== 2
          && !!currentFormItemSelector.profiling_freqence}
      >
        <FormItem label="频率(Hz)" name="profiling_freqence" initialData={99}
                  rules={[{ pattern: /^[1-9]\d*$/, message: '请输入正整数' }, {
                    required: true,
                    type: 'error',
                    message: '请输入频率',
                  }]}>
          <Input/>
        </FormItem>
      </VisFormItem>      {/* Python py-spy 特有配置字段 */}
      <VisFormItem visable={!!currentFormItemSelector.pyspy_profiler_path}>
        <FormItem label="py-spy路径" name="pyspy_profiler_path" initialData="/usr/local/bin/py-spy"
                  rules={[{ pattern: /^\/[\w\-.+]+(\/[\w\s\w-.+]+)*\/?$/, message: '请输入正确的文件路径' },
                    { required: true, type: 'error', message: '请输入py-spy可执行文件路径' }]}
                  help="py-spy可执行文件的完整路径，默认为 /usr/local/bin/py-spy">
          <Input placeholder="/usr/local/bin/py-spy"/>
        </FormItem>
      </VisFormItem>


      <VisFormItem visable={!!currentFormItemSelector.pyspy_frequency}>
        <FormItem label="采样频率(Hz)" name="profiling_freqence" initialData={100}
                  rules={[{ pattern: /^[1-9]\d*$/, message: '请输入正整数' }, {
                    required: true,
                    type: 'error',
                    message: '请输入采样频率',
                  }]}
                  help="py-spy采样频率，建议范围10-1000Hz，默认100Hz">
          <Input placeholder="100"/>
        </FormItem>
      </VisFormItem>

      <VisFormItem visable={!!currentFormItemSelector.pyspy_native_stack}>
        <FormItem initialData={false} label="原生栈跟踪" name="pyspy_native_stack"
                  help="是否包含原生C/C++栈信息，启用后可以看到Python扩展模块的调用栈">
          <Switch customValue={[true, false]} label={['启用', '禁用']}></Switch>
        </FormItem>
      </VisFormItem>


      {/* <VisFormItem
        visable={(featureEnable(currentAgent?.version, FEATURE_MINIMAL_VERSION.profilingByContainer))
          && (!!currentFormItemSelector.sourcefilepath_enable)
          && (noUnavailableFeatures(MACHINE_FEATURES.PERF_SCRIPT_ENABLE))}>
        <FormItem initialData={false} label="调试信息采集" name="sourcefilepath_enable"
                  help="开启后会略微影响运行速度,需要二进制文件含有Debug信息">
          <Switch customValue={[true, false]} label={['开启', '关闭']}></Switch>
        </FormItem>
      </VisFormItem> */}
      <VisFormItem visable={!!currentFormItemSelector.container_name && currentAgent?.version !== ''}>
        <FormItem label="容器名" name="container_name"
                  rules={[{ required: true, type: 'error', message: '请输入容器名' }]}>
          <Input/>
        </FormItem>
      </VisFormItem>
      <Divider
       align="center"
       layout="horizontal"
      ><Link
        underline = {false}
        hover = {''}
        onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
      >{
        <>
          高级选项
          {(showAdvancedOptions) ? <ChevronUpIcon/> : <ChevronDownIcon/>
          }
        </>
        }</Link></Divider>
      <VisFormItem visable={currentFormItemSelector.pprof_mem_leak_wide && showAdvancedOptions}>
      <FormItem label="时长(秒)" name="duration" initialData={0}
        help="默认为0，即采样当前时刻内存情况，设置其他数即为未来时间段内的内存变化情况。"
        rules={[{ pattern: /^[0-9]\d*$/, message: '请输入正整数' }, { required: true, type: 'error', message: '请输入pid' }]}>
        <Input />
      </FormItem>
      </VisFormItem>
      <VisFormItem
        visable={(
          Form.useWatch('perf_wide', form) === 'pid'
            && !!currentFormItemSelector.record_subprocess)
          && (featureEnable(currentAgent?.version, FEATURE_MINIMAL_VERSION.record_process))
          && noUnavailableFeatures(MACHINE_FEATURES.OPTION_PID_SUBPROCESS)
          && showAdvancedOptions}>
        <FormItem initialData={false} label="子进程" name="record_subprocess"
                  help="收集Pid子进程数据">
          <Switch customValue={[true, false]} label={['开启', '关闭']}></Switch>
        </FormItem>
      </VisFormItem>
        <VisFormItem visable={
        (Form.useWatch('profiler_type', form) !== 2)
        && !!currentFormItemSelector.callgraph
        && (featureEnable(currentAgent?.version, FEATURE_MINIMAL_VERSION.dwarf_lbr_callgraph_option)
        ) && showAdvancedOptions
      }>
        <FormItem
          label="栈回溯方式"
          name="callgraph"
          initialData='fp'
          rules={[{ required: true, type: 'error', message: '请选择栈回溯方式' }]}
          help="当gcc编译时使用--fomit-frame-pointer参数时选择dwarf">
          <Radio.Group>
            <Radio.Button value="fp">fp</Radio.Button>
            <Radio.Button value="dwarf">dwarf</Radio.Button>
          </Radio.Group>
        </FormItem>
      </VisFormItem>
      <VisFormItem visable={currentFormItemSelector.pprof_mem_leak_wide && showAdvancedOptions}>
      <FormItem label="源文件路径" name="pprofmemsourcepath" initialData={''}
        help="如果项目代码文件夹位置有所变动，即已经移动或删除。请填写当前可寻找到的项目文件夹绝对路径，请注意，项目文件夹名需一致。"
        rules={[{ pattern: /^\/[\w\-.+]+(\/[\w\w-.+]+)*\/?$/, message: '请输入正确的文件目录' }]}>
        <Input />
      </FormItem>
      </VisFormItem>
      <VisFormItem visable={currentFormItemSelector.task_type === 13 && showAdvancedOptions}>
        <FormItem label="采样信号" name="sampling_signal" initialData={12}
                  rules={[{ pattern: /^[1-9]\d*$/, message: '请输入正整数' }, {
                    required: true,
                    type: 'error',
                    message: '请输入采样信号',
                  }]}
                  help="用于控制采样开始和结束的信号编号，默认为12（SIGUSR2）">
          <Input placeholder="12"/>
        </FormItem>
      </VisFormItem>
      <VisFormItem
        visable={
          Form.useWatch('perf_wide', form) === 'system'
          && (!!currentFormItemSelector.container_type)
          && (featureEnable(currentAgent?.version, FEATURE_MINIMAL_VERSION.profilingByContainer))
          && noUnavailableFeatures(MACHINE_FEATURES.OPTION_CONTAINER_MODE)
          && showAdvancedOptions}>
        <FormItem initialData={0} label="容器视角" name="container_type"
                  help="雨滴Agent需要能访问到容器运行时">
          <Switch customValue={[1, 0]} label={['开启', '关闭']}></Switch>
        </FormItem>
      </VisFormItem>
      <VisFormItem
        visable={(!!currentFormItemSelector.annotate_enable_check)
          && (featureEnable(currentAgent?.version, FEATURE_MINIMAL_VERSION.annotate))
          && (noUnavailableFeatures(MACHINE_FEATURES.PERF_ANNOTATE_ENABLE))
          && showAdvancedOptions}>
        <FormItem initialData={false} label="热点汇编关联" name="annotate_enable"
                  help="开启后会略微影响运行速度,需要二进制文件含有Debug信息">
          <Switch customValue={[true, false]} label={['开启', '关闭']}></Switch>
        </FormItem>
      </VisFormItem>
      <VisFormItem visable={ showAdvancedOptions}>
        <FormItem initialData={1} label="提前终止" name="privilege"
                  help="剩余资源不足将提前终止采集">
          <Switch customValue={[1, 0]} label={['允许', '拒绝']}></Switch>
        </FormItem>
      </VisFormItem>
      <VisFormItem visable={ showAdvancedOptions}>
        <FormItem label="执行计划" name="schedule_type" initialData="none">
          <Radio.Group>
            <Radio.Button value="none">立即执行</Radio.Button>
            <Radio.Button value="one-time">指定时间</Radio.Button>
            <Radio.Button value="cron">循环</Radio.Button>
          </Radio.Group>
        </FormItem>
      </VisFormItem>
      <VisFormItem visable={Form.useWatch('schedule_type', form) === 'one-time'} style={{ marginLeft: 5 }}>
        <FormItem label="定时时间"
                  name="schedule_time"
                  rules={[
                    { required: true, type: 'error', message: '请选择执行时间' },
                    { validator: v => dayjs(v).isAfter(dayjs()), message: '请选择未来的时间' },
                  ]}>
          <DatePicker enableTimePicker allowInput clearable
                      valueType="time-stamp"
                      disableDate={{
                        before: dayjs().subtract(1, 'day')
                          .format(),
                      }}
                      onPick={(value) => {
                        setSelectTime(dayjs(value));
                      }}
                      timePickerProps={{ disableTime }}
          />
        </FormItem>
      </VisFormItem>
      <VisFormItem visable={Form.useWatch('schedule_type', form) === 'cron'} style={{ marginLeft: 5 }}>
        <FormItem label="Cron 表达式" name="schedule_time" help="在线生成：https://crontab.guru/" rules={[
          { validator: v => cron(v)?.isValid(), message: '请输入正确的 Cron 表达式' },
        ]}>
          <Input placeholder="请输入合法的 Cron 表达式"></Input>
        </FormItem>
      </VisFormItem>
      <FormItem style={{ marginLeft: 100 }}>
        <Button theme="primary" type="submit" style={{ marginRight: 50 }}>
          提交
        </Button>
        <Button type="reset" style={{ marginRight: 50 }}>重置</Button>
        <Button type="button" onClick={() => genCurlCommand()}>Api调用</Button>
      </FormItem>
    </Form>
    <Dialog
      closeBtn
      closeOnEscKeydown
      closeOnOverlayClick
      cancelBtn={false}
      footer
      header={
        <>
        <Text>Api调用命令</Text>
        <Link
          href={'https://iwiki.woa.com/p/4012533992'}
          size="small"
          theme="primary"
          target={'_blank'}
          style={{ marginLeft: 10 }}>
        帮助</Link>
        </>}
      mode="modal"
      onClose={ () => {
        setShowCurlCommand(false);
      }}
      confirmBtn={false}
      placement="top"
      preventScrollThrough
      showOverlay
      visible={showCurlCommand}
      theme="default"
    >
      <Text
        copyable>
      {curlCommand}</Text>
  </Dialog>
  <Dialog
      closeBtn
      closeOnEscKeydown
      closeOnOverlayClick
      cancelBtn={false}
      footer
      header={
        <>
        <Text>GperfTools 启动脚本模板</Text>
        <Link
          href={'https://iwiki.woa.com/p/4016416717'}
          size="small"
          theme="primary"
          target={'_blank'}
          style={{ marginLeft: 10 }}>
        帮助</Link>
        </>}
      mode="modal"
      onClose={ () => {
        setShowScriptTemplate(false);
      }}
      confirmBtn={false}
      placement="top"
      preventScrollThrough
      showOverlay
      visible={showScriptTemplate}
      theme="default"
      width="800px"
    >
      <div style={{ marginBottom: 16 }}>
        <Text theme="warning" size="small">
          请将 TARGET_PROGRAM 替换为您的实际程序执行脚本
        </Text>
      </div>
      <div style={{ position: 'relative' }}>
        <Text
          style={{
            whiteSpace: 'pre-wrap',
            fontFamily: 'monospace',
            display: 'block',
            backgroundColor: '#f5f5f5',
            padding: '12px',
            borderRadius: '4px',
            fontSize: '12px',
            marginBottom: '12px'
          }}>
        {SCRIPT}</Text>
        <div style={{ textAlign: 'right' }}>
          <Button
            theme="primary"
            onClick={() => {
              navigator.clipboard.writeText(SCRIPT);
              MessagePlugin.success('脚本已复制到剪贴板');
            }}
          >
            复制脚本
          </Button>
        </div>
      </div>
  </Dialog>
  </>
  );
};

StartTaskForm.propTypes = {
  agents: PropTypes.array,
  onClick: PropTypes.func,
  genCurlCommand: PropTypes.func,
  presetParams: PropTypes.object, // 预设参数
};

export default StartTaskForm;
